import { ChangeDetectionStrategy, Component, computed, inject, input, Input, model, OnDestroy, OnInit, signal } from "@angular/core";
import { ListingColInterface } from "../../../baseListing/interfaces/listing-config.interface";
import { TableComponent } from "../table/table.component";
import { TranslationService } from "../../../../translations/translation.service";
import { Subject, takeUntil } from "rxjs";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SvgIconComponent } from "../../../../tools/svg-icon/components/svg-icon/svg-icon.component";
import { OverlayPanelComponent } from "../../../../shared/modules/overlay-panel/overlay-panel.config";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "sxw-table-column-toggle",
  templateUrl: "./table-column-toggle.component.html",
  styleUrls: ["./table-column-toggle.component.scss"],
  imports: [CommonModule, TranslateModule, FormsModule, SvgIconComponent, OverlayPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnToggleComponent implements OnInit, OnDestroy {
  columns = model.required<ListingColInterface[]>();
  _columns = computed(() => [...this.columns()]);

  hiddenCols = computed(() => {
    return this.columns().filter(col => col?.hidden && col?.label?.match(new RegExp(this.escapedSearchText(), "i")));
  });
  visibleCols = computed(() => {
    return this.columns().filter(col => !col?.hidden && col?.label?.match(new RegExp(this.escapedSearchText(), "i")));
  });

  private _unsubscribe = new Subject<void>();
  searchText = signal("");
  /**
   * escape some characters like '.' by converting them to normal string
   */
  escapedSearchText = signal("");

  private tableComponent = inject(TableComponent);
  private transService = inject(TranslationService);

  ngOnInit() {
    this.translateColumnLabels();
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  trackCols(_: number, col: ListingColInterface) {
    return col.label;
  }

  toggleCol(column: ListingColInterface) {
    column.hidden = !column.hidden;
    let cols = { ...this.tableComponent.userOptions().cols };
    cols[column.id!] = {
      ...cols[column.id!],
      hidden: column.hidden,
    };
    this.tableComponent.updateUserOptions({ cols }).flushUserOptions();
  }

  onSearch(label: string) {
    this.escapedSearchText.set(label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  }

  /**
   * Translate column labels to work correctly in search
   * @private
   */
  private translateColumnLabels() {
    this.transService
      .waitUntilTranslationsEmits()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(_ => {
        this.columns.update(columns => {
          return columns.map(col => ({
            ...col,
            label: col.label ? this.transService.instant(col.label) : col.label,
          }));
        });
      });
  }
}
